import React, {
  FC, useCallback, useContext, useMemo, useState,
} from 'react';
import { Webinar } from 'models/webinars';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import { ButtonBase, ClickAwayListener, useMediaQuery } from '@material-ui/core';
import { UserSessionContext } from 'components/UserSession';
import useChatVisibility from 'components/twilio/hooks/useChatVisibility';
import Slido from 'components/icons/Slido';
import { useFabBottomValue } from 'components/BackToTopButton';

const StyledSlidoButton = styled.div<{ $expanded: boolean; $bottom: number }>`
  position: fixed;
  bottom: ${({ $bottom }) => `${$bottom}px`};
  right: 20px;
  cursor: pointer;
  writing-mode: vertical-lr;
  background: #06AF97;
  color: white;
  border-radius: ${({ $expanded }) => ($expanded ? 'none' : '8px')};
  box-shadow: black 0 0 5px;
  margin-top: 30px;
  width: ${({ $expanded }) => ($expanded ? '320px' : '56px')};
  height: ${({ $expanded }) => ($expanded ? '590px' : '56px')};
  transition: all 200ms ease-in-out;
  max-height: calc(100% - ${({ $bottom }) => $bottom + 100}px);
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledSlidoIcon = styled(Slido)`
  width: 28px;
  height: 28px;
  font-size: 28px;
`;

const StyledMobileSlidoButton = styled(ButtonBase)<{ $displayChat: boolean }>`
  position: fixed;
  right: 20px;
  bottom: 86px;
  z-index: 1100;
  background: #81AC4F;
  color: white;
  border-radius: 100%;
  overflow: hidden;
  box-shadow: 0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%);
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledMobileSlidoContainer = styled.div<{ $expanded: boolean }>`
  position: fixed;
  max-width: calc(100% - 20px);
  max-height: calc(100% - 50px);
  height: ${({ $expanded }) => ($expanded ? '440px' : '0')};
  right: 10px;
  left: 10px;
  bottom: 10px;
  z-index: 1200;
  transition: all 200ms ease-in-out;
  box-shadow: 0 0 5px dimgrey;
`;

const StyledBackdrop = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1150;
`;

export interface WebinarFloatingSlidoProps {
  webinar: Webinar
}

const WebinarFloatingSlido: FC<WebinarFloatingSlidoProps> = ({ webinar }) => {
  const {
    userSession: {
      name,
      email,
      company,
    },
  } = useContext(UserSessionContext);
  const locale = useSelector(selectLocale);
  const slidoUrl = webinar?.translations?.[locale]?.slido_url;

  const isDesktop = useMediaQuery('(min-width: 960px)');
  const [showSlido, setShowSlido] = useState(null);
  const toggleShowSlido = useCallback(() => {
    setShowSlido(!(showSlido ?? isDesktop));
  }, [isDesktop, showSlido]);

  const closeSlido = useCallback(() => {
    setShowSlido(false);
  }, []);

  const slidoContent = useMemo(() => {
    if (slidoUrl == null) return null;
    const url = new URL(slidoUrl);
    url.searchParams.append('user_name', name);
    url.searchParams.append('user_email', email);
    url.searchParams.append('user_company', company);
    return (
      <iframe src={url.href} height="100%" width="100%" frameBorder="0" title="Slido" />
    );
  }, [company, email, name, slidoUrl]);

  const displayChat = useChatVisibility();
  const fabBottomValue = useFabBottomValue() + 58;

  const features = useSelector((s: any) => s.eventConfigs.features);
  const enabledSlido = useMemo(() => features?.includes('webinarSlido') ?? false, [features]);

  if (!enabledSlido || slidoUrl == null) return null;
  return (
    <>
      {isDesktop ? (
        <ClickAwayListener onClickAway={closeSlido}>
          <StyledSlidoButton onClick={toggleShowSlido} $expanded={showSlido ?? isDesktop} $bottom={fabBottomValue}>
            {(showSlido ?? isDesktop) ? slidoContent : <StyledSlidoIcon />}
          </StyledSlidoButton>
        </ClickAwayListener>
      ) : (
        <>
          <ClickAwayListener onClickAway={closeSlido}>
            <div>
              <StyledMobileSlidoContainer $expanded={showSlido}>
                {slidoContent}
              </StyledMobileSlidoContainer>
              <StyledMobileSlidoButton $displayChat={displayChat} onClick={toggleShowSlido}>
                <StyledSlidoIcon />
              </StyledMobileSlidoButton>
            </div>
          </ClickAwayListener>
        </>
      )}
    </>
  );
};

export default WebinarFloatingSlido;
